import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, limit } from "firebase/firestore";
import { db } from "../../utils/firebase";
import ResponsiveImage from "../common/ResponsiveImage";
import { logofull, logofullwhite } from "../../assets";

const navItems = [
  { label: "Home", path: "/", flyout: null },
  { label: "About Us", path: "/about", flyout: null },
  {
    label: "FACILITIES",
    path: "/facilities",
    flyout: {
      sections: [
        {
          heading: "Cricket Arena",
          links: [
            { label: "Cricket", path: "/facilities/cricket" },
            { label: "Baseball", path: "/facilities/baseball" },
            { label: "Volleyball", path: "/facilities/volleyball" },
            { label: "Futsal", path: "/facilities/futsal" },
          ],
        },
        {
          heading: "Badminton Arena",
          links: [
            { label: "Badminton Courts", path: "/facilities/badminton" },
            { label: "Pickleball", path: "/facilities/pickleball" },
          ],
        },
        {
          heading: "Dead shot",
          links: [
            { label: "Rifle Shooting", path: "/facilities/Rifle Shooting" },
            { label: "Archery Range", path: "/facilities/archery" },
          ],
        },
        {
          heading: "Sports Hub",
          links: [
            { label: "Table Tennis Table", path: "/facilities/Table Tennis" },
            { label: "Pool Table", path: "/facilities/8-Ball Pool" },
            { label: "Soccer Arcade", path: "/facilities/soccer-arcade" },
            { label: "Basketball Arcade", path: "/facilities/basketball-arcade" },
            {label: "Skating", path: "/facilities/skating"}
          ],
        },
        {
          heading: "Fitness and Martial Arts",
          links: [
            { label: "Dancing Studio", path: "/facilities/dancing" },
            { label: "Martial Arts Studio", path: "/facilities/Martial Arts" },
            { label: "Gymnastics & Aerobics", path: "/facilities/Gymnastics & Aerobics" },
            { label: "Leadership Training", path: "/facilities/Leadership Training" },
            { label: "Yoga & Physical Therapy", path: "/facilities/Yoga & Physical Therapy" }
          ],
        },
      ],
    },
  },
  { label: "Blog", path: "/blog", flyout: null },
  { label: "Gallery", path: "/gallery", flyout: null },
  { label: "Contact", path: "/contact", flyout: null },
];

const Navbar = () => {
  const [activeItem, setActiveItem] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [mobileCategoryExpanded, setMobileCategoryExpanded] = useState(null);
  const [facilityImages, setFacilityImages] = useState([]);
  const leaveTimer = useRef(null);

  // Pull a couple of random shots from the real gallery for the FACILITIES
  // flyout instead of a hardcoded placeholder image. Runs once - the navbar
  // stays mounted across route changes, so this isn't refetched per page.
  useEffect(() => {
    let active = true;
    async function loadFacilityImages() {
      try {
        const snap = await getDocs(
          query(collection(db, "galleryImages"), limit(30))
        );
        if (!active) return;
        const all = snap.docs
          .map((d) => d.data())
          .filter((d) => d.imageUrl);
        if (all.length === 0) return;
        // Fisher-Yates-ish random pick of up to 2 distinct images.
        const shuffled = [...all].sort(() => Math.random() - 0.5);
        setFacilityImages(
          shuffled.slice(0, 2).map((img) => ({
            src: img.imageUrl,
            alt: img.title || "Viroosh Sports Complex facility",
          }))
        );
      } catch (e) {
        console.error("[Navbar] failed to load facility images:", e);
      }
    }
    loadFacilityImages();
    return () => {
      active = false;
    };
  }, []);

  const handleMouseEnter = (label) => {
    clearTimeout(leaveTimer.current);
    setActiveItem(label);
  };

  const handleDesktopMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setActiveItem(null), 120);
  };

  const activeFlyout = navItems.find((i) => i.label === activeItem)?.flyout;

  const toggleMobileExpanded = (label) => {
    setMobileExpanded((prev) => (prev === label ? null : label));
    // Collapse any open facility category whenever the parent section is toggled
    setMobileCategoryExpanded(null);
  };

  const toggleMobileCategory = (heading) =>
    setMobileCategoryExpanded((prev) => (prev === heading ? null : heading));

  const handleHamburgerClick = () => {
    setMobileOpen((prev) => !prev);
    setMobileExpanded(null);
    setMobileCategoryExpanded(null);
  };

  return (
    <div className="absolute top-0 left-0 right-0 z-50">
      <nav
        className={`mx-4 md:mx-10 mt-4 border border-black/10 bg-white/10 backdrop-blur-sm flex items-center justify-between px-6 md:px-8 py-4 ${
          activeFlyout || mobileOpen ? "rounded-t-xl" : "rounded-xl"
        }`}
      >
        <Link to="/" className="shrink-0">
          <img src={logofullwhite} alt="Logo" className="h-10" />
        </Link>

        <ul
          className="hidden md:flex items-center gap-8 text-md font-semibold uppercase tracking-widest text-white/70"
          onMouseLeave={handleDesktopMouseLeave}
        >
          {navItems.map((item) => (
            <li key={item.label}>
              {item.flyout ? (
                <button
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  className={`flex items-center gap-1 transition-colors hover:text-white ${
                    activeItem === item.label ? "text-red-500" : ""
                  }`}
                >
                  {item.label}
                  <svg
                    className={`w-3 h-3 transition-transform duration-200 ${
                      activeItem === item.label ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              ) : (
                <Link
                  to={item.path}
                  onMouseEnter={() => setActiveItem(null)}
                  className="hover:text-white transition-colors"
                >
                  {item.label}
                </Link>
              )}
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <Link
            to="/booking"
            className="px-5 py-2 bg-red-700 hover:bg-red-600 text-white text-xs font-bold uppercase tracking-widest transition-colors"
          >
            Book Now
          </Link>
          <Link
            to="/uploadpayment"
            className="px-5 py-2 bg-red-700 hover:bg-red-600 text-white text-xs font-bold uppercase tracking-widest transition-colors"
          >
            Upload Payment
          </Link>
        </div>

        <button
          type="button"
          className="md:hidden flex flex-col justify-center gap-1.5 p-2 z-30 relative"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={handleHamburgerClick}
        >
          <span
            className={`block w-6 h-0.5 bg-white transition-all duration-300 origin-center ${mobileOpen ? "rotate-45 translate-y-[7px]" : ""}`}
          />
          <span
            className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? "opacity-0 scale-x-0" : ""}`}
          />
          <span
            className={`block w-4 h-0.5 bg-white transition-all duration-300 origin-center ${mobileOpen ? "-rotate-45 -translate-y-[7px] w-6" : ""}`}
          />
        </button>
      </nav>

      {/* Desktop flyout */}
      <div
        className={`hidden md:block mx-4 md:mx-10 border-x border-b border-black/10 bg-black/15 backdrop-blur-md transition-all duration-300 overflow-hidden rounded-b-xl ${
          activeFlyout
            ? "max-h-96 opacity-100"
            : "max-h-0 opacity-0 pointer-events-none"
        }`}
        onMouseEnter={() => clearTimeout(leaveTimer.current)}
        onMouseLeave={handleDesktopMouseLeave}
      >
        {activeFlyout && (
          <div className="px-8 py-8">
            {activeFlyout.eventCards ? (
              <div className="flex justify-center items-start gap-6 w-full">
                {activeFlyout.eventCards.map((card, i) => (
                  <div
                    key={i}
                    className="w-60 group cursor-pointer flex flex-col items-center"
                  >
                    <div className="overflow-hidden rounded-sm h-40 w-full mb-3">
                      <img
                        src={card.src}
                        alt={card.label}
                        className="w-full h-full object-cover group-hover:scale-105 transition-all duration-300"
                      />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/70 group-hover:text-red-500 transition-colors text-center">
                      {card.label}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex gap-6 items-start">
                <div className="flex gap-8 flex-1">
                  {activeFlyout.sections.map((section) => (
                    <div key={section.heading}>
                      <p className="text-white/30 text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5 whitespace-nowrap">
                        {section.heading}
                      </p>
                      {section.columns === 2 ? (
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          {section.links.map((link) => (
                            <Link
                              key={link.label}
                              to={link.path}
                              onClick={() => setActiveItem(null)}
                              className="text-xs font-semibold uppercase tracking-widest text-white/70 hover:text-red-500 transition-colors"
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <ul className="flex flex-col gap-1">
                          {section.links.map((link) => (
                            <li key={link.label}>
                              <Link
                                to={link.path}
                                onClick={() => setActiveItem(null)}
                                className="text-xs font-semibold uppercase tracking-widest text-white/70 hover:text-red-500 transition-colors whitespace-nowrap"
                              >
                                {link.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
                {activeItem === "FACILITIES" && facilityImages.length > 0 && (
                  <div className="flex gap-3 shrink-0">
                    {facilityImages.map((img, i) => (
                      <div
                        key={i}
                        className="w-60 h-40 overflow-hidden rounded-sm"
                      >
                        <ResponsiveImage
                          src={img.src}
                          alt={img.alt}
                          desktopWidth={300}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden mx-4 -mt-px border-x border-b border-black/10 bg-white/10 backdrop-blur-sm overflow-hidden transition-all duration-300 rounded-b-xl ${
          mobileOpen
            ? "max-h-screen opacity-100"
            : "max-h-0 opacity-0 pointer-events-none"
        }`}
      >
        <ul className="flex flex-col divide-y divide-white/10">
          {navItems.map((item) => (
            <li key={item.label}>
              {item.flyout ? (
                <>
                  <button
                    type="button"
                    onClick={() => toggleMobileExpanded(item.label)}
                    className="w-full flex items-center justify-between px-6 py-4 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white transition-colors"
                  >
                    {item.label}
                    <svg
                      className={`w-3 h-3 transition-transform duration-200 ${mobileExpanded === item.label ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-200 ${mobileExpanded === item.label ? "max-h-[40rem]" : "max-h-0"}`}
                  >
                    {item.flyout.sections && item.flyout.sections.length > 0 ? (
                      <div className="px-6 pb-2 flex flex-col divide-y divide-white/5">
                        {item.flyout.sections.map((section) => {
                          const isCategoryOpen = mobileCategoryExpanded === section.heading;
                          return (
                            <div key={section.heading} className="py-1">
                              <button
                                type="button"
                                onClick={() => toggleMobileCategory(section.heading)}
                                className="w-full flex items-center justify-between py-2 text-left"
                              >
                                <span className="text-white/40 text-[10px] uppercase tracking-[0.25em] font-bold">
                                  {section.heading}
                                </span>
                                <svg
                                  className={`w-2.5 h-2.5 text-white/40 transition-transform duration-200 ${isCategoryOpen ? "rotate-180" : ""}`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2.5}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </button>
                              <div
                                className={`overflow-hidden transition-all duration-200 ${isCategoryOpen ? "max-h-96 pb-3" : "max-h-0"}`}
                              >
                                {section.columns === 2 ? (
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                    {section.links.map((link) => (
                                      <Link
                                        key={link.label}
                                        to={link.path}
                                        onClick={() => setMobileOpen(false)}
                                        className="text-white/60 hover:text-red-500 text-[10px] font-semibold uppercase tracking-widest transition-colors"
                                      >
                                        {link.label}
                                      </Link>
                                    ))}
                                  </div>
                                ) : (
                                  <ul className="flex flex-col gap-2">
                                    {section.links.map((link) => (
                                      <li key={link.label}>
                                        <Link
                                          to={link.path}
                                          onClick={() => setMobileOpen(false)}
                                          className="text-white/60 hover:text-red-500 text-[10px] font-semibold uppercase tracking-widest transition-colors"
                                        >
                                          {link.label}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="px-6 pb-4">
                        <Link
                          to={item.path}
                          onClick={() => setMobileOpen(false)}
                          className="text-white/60 hover:text-red-500 text-xs font-semibold uppercase tracking-widest transition-colors"
                        >
                          View All Events
                        </Link>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center px-6 py-4 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white transition-colors"
                >
                  {item.label}
                </Link>
              )}
            </li>
          ))}
          <li className="px-6 py-4 flex flex-col gap-2">
            <Link
              to="/booking"
              onClick={() => setMobileOpen(false)}
              className="block text-center px-5 py-3 bg-red-700 hover:bg-red-600 text-white text-xs font-bold uppercase tracking-widest transition-colors"
            >
              Book Now
            </Link>
            <Link
              to="/uploadpayment"
              onClick={() => setMobileOpen(false)}
              className="block text-center px-5 py-3 bg-red-700 hover:bg-red-600 text-white text-xs font-bold uppercase tracking-widest transition-colors"
            >
              Upload Payment
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Navbar;